import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Bot,
  Play,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  RefreshCw,
  Filter
} from 'lucide-react'
import {
  getAgentStatus,
  getAgentRuns,
  getDiscrepancies,
  triggerAgent,
  resolveDiscrepancy,
  dismissDiscrepancy
} from '../../services/api'
import clsx from 'clsx'
import PageHeader from '../../components/PageHeader'

// =============================================================================
// AGENT STATUS CARD
// =============================================================================

function AgentCard({ agent, ranchId, onTrigger }) {
  const scheduleLabels = {
    daily: 'Daily',
    weekly: 'Weekly',
    monthly: 'Monthly',
  }

  const statusColor = agent.failure_count > 0 
    ? 'border-red-200 bg-red-50' 
    : agent.success_count > 0 
      ? 'border-green-200 bg-green-50'
      : 'border-gray-200 bg-gray-50'

  return (
    <div className={clsx('card p-4 border-2 rounded-xl', statusColor)}>
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className={clsx(
            'p-2.5 rounded-xl ring-1',
            agent.failure_count > 0 ? 'bg-red-100 ring-red-200' : 'bg-primary-100 ring-primary-200'
          )}>
            <Bot className={clsx(
              'h-5 w-5',
              agent.failure_count > 0 ? 'text-red-600' : 'text-primary-600'
            )} />
          </div>
          <div>
            <h3 className="font-medium text-gray-900">
              {agent.agent_name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
            </h3>
            <p className="text-sm text-gray-500">{agent.description}</p>
          </div>
        </div>
        <button
          onClick={() => onTrigger(agent.agent_name)}
          className="p-2 hover:bg-white rounded-lg"
          title="Run now"
        >
          <Play className="h-4 w-4 text-gray-600" />
        </button>
      </div>
      
      <div className="mt-4 grid grid-cols-3 gap-4 text-center text-sm">
        <div>
          <div className="text-gray-500">Schedule</div>
          <div className="font-medium">{scheduleLabels[agent.schedule] || agent.schedule}</div>
        </div>
        <div>
          <div className="text-gray-500">Last Run</div>
          <div className="font-medium">
            {agent.last_run 
              ? new Date(agent.last_run).toLocaleDateString() 
              : 'Never'}
          </div>
        </div>
        <div>
          <div className="text-gray-500">Success Rate</div>
          <div className={clsx(
            'font-medium',
            agent.success_rate >= 0.9 ? 'text-green-600' : 
            agent.success_rate >= 0.7 ? 'text-amber-600' : 'text-red-600'
          )}>
            {(agent.success_rate * 100).toFixed(0)}%
          </div>
        </div>
      </div>
    </div>
  )
}

// =============================================================================
// RUN HISTORY TABLE
// =============================================================================

function RunHistory({ runs }) {
  if (!runs || runs.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        No agent runs recorded yet
      </div>
    )
  }

  const statusConfig = {
    success: { icon: CheckCircle, color: 'text-green-600' },
    failed: { icon: XCircle, color: 'text-red-600' },
    running: { icon: RefreshCw, color: 'text-blue-600 animate-spin' },
    partial: { icon: AlertTriangle, color: 'text-amber-600' },
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full">
        <thead>
          <tr className="text-left text-sm text-earth-600 border-b border-earth-200">
            <th className="pb-3 font-medium">Agent</th>
            <th className="pb-3 font-medium">Started</th>
            <th className="pb-3 font-medium">Duration</th>
            <th className="pb-3 font-medium">Status</th>
            <th className="pb-3 font-medium">Records</th>
          </tr>
        </thead>
        <tbody className="divide-y">
          {runs.map((run) => {
            const config = statusConfig[run.status] || statusConfig.partial
            const IconComponent = config.icon
            const duration = run.completed_at 
              ? Math.round((new Date(run.completed_at) - new Date(run.started_at)) / 1000)
              : null
            
            return (
              <tr key={run.id} className="text-sm">
                <td className="py-3">
                  <span className="font-medium text-gray-900">
                    {run.agent_name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </span>
                </td>
                <td className="py-3 text-gray-600">
                  {new Date(run.started_at).toLocaleString()}
                </td>
                <td className="py-3 text-gray-600">
                  {duration !== null ? `${duration}s` : '-'}
                </td>
                <td className="py-3">
                  <div className="flex items-center gap-1.5">
                    <IconComponent className={clsx('h-4 w-4', config.color)} />
                    <span className={config.color}>{run.status}</span>
                  </div>
                </td>
                <td className="py-3 text-gray-600">
                  {run.records_processed}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

// =============================================================================
// DISCREPANCY LIST
// =============================================================================

function DiscrepancyList({ ranchId }) {
  const queryClient = useQueryClient()
  const [statusFilter, setStatusFilter] = useState('open')
  
  const { data: flags, isLoading } = useQuery({
    queryKey: ['discrepancies', ranchId, statusFilter],
    queryFn: () => getDiscrepancies(ranchId, statusFilter),
  })

  const resolveMutation = useMutation({
    mutationFn: ({ flagId, notes }) => resolveDiscrepancy(flagId, notes, ranchId),
    onSuccess: () => {
      queryClient.invalidateQueries(['discrepancies', ranchId])
    }
  })

  const dismissMutation = useMutation({
    mutationFn: (flagId) => dismissDiscrepancy(flagId),
    onSuccess: () => {
      queryClient.invalidateQueries(['discrepancies', ranchId])
    }
  })

  const severityConfig = {
    critical: { bg: 'bg-red-100', text: 'text-red-700', badge: 'bg-red-500' },
    warning: { bg: 'bg-amber-100', text: 'text-amber-700', badge: 'bg-amber-500' },
    info: { bg: 'bg-blue-100', text: 'text-blue-700', badge: 'bg-blue-500' },
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <RefreshCw className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    )
  }

  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        <Filter className="h-4 w-4 text-gray-400" />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="text-sm border rounded-lg px-2 py-1"
        >
          <option value="open">Open</option>
          <option value="investigating">Investigating</option>
          <option value="resolved">Resolved</option>
          <option value="dismissed">Dismissed</option>
        </select>
      </div>

      {!flags || flags.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          No {statusFilter} discrepancies
        </div>
      ) : (
        <div className="space-y-3">
          {flags.map((flag) => {
            const config = severityConfig[flag.severity] || severityConfig.info
            
            return (
              <div key={flag.id} className={clsx('p-4 rounded-lg', config.bg)}>
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className={clsx(
                        'px-2 py-0.5 rounded text-xs font-medium text-white',
                        config.badge
                      )}>
                        {flag.severity}
                      </span>
                      <span className={clsx('font-medium', config.text)}>
                        {flag.flag_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </span>
                    </div>
                    <p className="text-sm text-gray-700 mt-1">{flag.description}</p>
                    <p className="text-xs text-gray-500 mt-2">
                      Detected: {new Date(flag.detected_at).toLocaleString()}
                    </p>
                  </div>
                  {flag.status === 'open' && (
                    <div className="flex gap-2">
                      <button
                        onClick={() => dismissMutation.mutate(flag.id)}
                        className="text-sm px-3 py-1 bg-white rounded hover:bg-gray-50"
                      >
                        Dismiss
                      </button>
                      <button
                        onClick={() => resolveMutation.mutate({ flagId: flag.id, notes: 'Resolved' })}
                        className="text-sm px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700"
                      >
                        Resolve
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

// =============================================================================
// MAIN AGENTS PAGE
// =============================================================================

export default function AgentsPage({ ranchId }) {
  const queryClient = useQueryClient()
  const [activeTab, setActiveTab] = useState('agents')
  
  const { data: agents, isLoading: agentsLoading } = useQuery({
    queryKey: ['agentStatus', ranchId],
    queryFn: () => getAgentStatus(ranchId),
  })

  const { data: runs, isLoading: runsLoading } = useQuery({
    queryKey: ['agentRuns', ranchId],
    queryFn: () => getAgentRuns(ranchId),
  })

  const triggerMutation = useMutation({
    mutationFn: (agentName) => triggerAgent(ranchId, agentName, true),
    onSuccess: () => {
      queryClient.invalidateQueries(['agentStatus', ranchId])
      queryClient.invalidateQueries(['agentRuns', ranchId])
    }
  })

  const tabs = [
    { id: 'agents', label: 'Agents', icon: Bot },
    { id: 'history', label: 'Run History', icon: Clock },
    { id: 'discrepancies', label: 'Discrepancies', icon: AlertTriangle },
  ]

  return (
    <div className="space-y-6">
      <PageHeader
        title="Agent Management"
        subtitle="Monitor and manage background agents"
        icon={Bot}
      />

      {/* Tabs */}
      <div className="border-b">
        <div className="flex gap-6">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={clsx(
                'flex items-center gap-2 pb-3 border-b-2 transition-colors',
                activeTab === tab.id
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              )}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'agents' && (
        <div>
          {agentsLoading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {agents?.map((agent) => (
                <AgentCard
                  key={agent.agent_name}
                  agent={agent}
                  ranchId={ranchId}
                  onTrigger={(name) => triggerMutation.mutate(name)}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'history' && (
        <div className="section-card">
          <div className="section-card-header">
            <h3 className="section-card-title">Recent Agent Runs</h3>
            <Clock className="h-5 w-5 text-earth-400" />
          </div>
          <div className="section-card-body">
            {runsLoading ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="h-6 w-6 animate-spin text-gray-400" />
              </div>
            ) : (
              <RunHistory runs={runs} />
            )}
          </div>
        </div>
      )}

      {activeTab === 'discrepancies' && (
        <div className="section-card">
          <div className="section-card-header">
            <h3 className="section-card-title">Discrepancy Flags</h3>
            <AlertTriangle className="h-5 w-5 text-earth-400" />
          </div>
          <div className="section-card-body">
            <DiscrepancyList ranchId={ranchId} />
          </div>
        </div>
      )}
    </div>
  )
}
